import { useQueries, useQuery } from '@tanstack/react-query'
import {
  queries,
  MealSummary,
  useMealSummaries,
} from '@tovala/browser-apis-combinedapi'
import { useMenu } from '@tovala/browser-apis-menu-delivery'

import { useUser } from 'contexts/user'
import { orderBy } from 'lodash-es'
import { UserTerm } from 'types/internal'

export function usePublicCurrentMenu(opts?: { enabled: boolean }) {
  const { enabled = true } = opts || {}

  const {
    data: publicTermStatuses = [],
    error: loadPublicTermStatusesError,
    isError: hasLoadPublicTermStatusesError,
  } = useQuery({ ...queries.termStatuses.public(), enabled })

  const subTermIDs = publicTermStatuses
    .filter((term) => term.readyForView)
    .map((term) => term.selectedSubTermID)

  const allMealSummaries = useQueries({
    queries: subTermIDs.map((subTermID) => ({
      ...queries.meals.summaries({ subTermID }),
      enabled,
    })),
  })

  const isLoadingMealSummaries = allMealSummaries.some(
    (mealSummaries) => mealSummaries.isLoading,
  )
  // On the rare occasion the current term has no meals (holiday break, for example),
  // we want the first term that has meals
  const currentMenuIndex = !isLoadingMealSummaries
    ? allMealSummaries.findIndex(
        (mealSummaries) => mealSummaries.data && mealSummaries.data.length > 0,
      )
    : -1

  const mealSummaries =
    currentMenuIndex > -1 ? allMealSummaries[currentMenuIndex]?.data : []

  return {
    error: loadPublicTermStatusesError,
    isError: hasLoadPublicTermStatusesError,
    mealSummaries: orderBy(mealSummaries, (meal) => meal.mainDisplayOrder),
    term:
      currentMenuIndex > -1 ? publicTermStatuses[currentMenuIndex] : undefined,
  }
}

export function useIsNoMenuTerm({ term }: { term: UserTerm }) {
  const { data: mealSummaries } = useMealSummaries({
    subTermID: term.selectedSubTermID,
  })

  const noMealsAvailable = mealSummaries && !mealSummaries.length

  return {
    isNoMenuTerm: !!(term.specialEvent && noMealsAvailable),
    specialEvent: term.specialEvent,
  }
}

export function useMenuComponents({ subTermID }: { subTermID: string }) {
  const { user } = useUser()

  const { data: menuComponents, isPending: isLoadingMenu } = useMenu({
    subTermID,
    userID: user.id,
  })

  return {
    components: menuComponents?.components ?? [],
    isPending: isLoadingMenu,
  }
}

export function useUnavailableMealSelectionsForDeliveryDayChange({
  term,
}: {
  term: UserTerm
}) {
  let unavailableMealSelectionIDs: number[] = []

  const alternateSubTerm = term.subTerms.find(
    (subTerm) =>
      subTerm.subTermID !== term.selectedSubTermID && subTerm.isAvailable,
  )

  const { data: mealSummaries = [] } = useMealSummaries({
    subTermID: term.selectedSubTermID,
  })

  const { data: alternateMealSummaries = [] } = useMealSummaries({
    subTermID: alternateSubTerm?.subTermID,
  })

  if (alternateSubTerm) {
    const uniqueSelectedMealIDs = Array.from(
      new Set(term.mealSelections.map((selection) => selection.mealID)),
    )

    const alternateSubTermAvailableMealIDs = alternateMealSummaries
      .filter((meal) => !meal.isSoldOut)
      .map((meal) => meal.id)
    unavailableMealSelectionIDs = uniqueSelectedMealIDs.filter(
      (mealID) => !alternateSubTermAvailableMealIDs.includes(mealID),
    )
  }

  const unavailableMealSelections = unavailableMealSelectionIDs
    .map((mealID) => {
      return mealSummaries.find((meal) => meal.id === mealID)
    })
    .filter((meal): meal is MealSummary => !!meal)

  return { unavailableMealSelections }
}
